import { pldtConfig } from "../config";

const SAFE_LINK_PROTOCOLS = new Set(["http:", "https:", "mailto:", "tel:"]);

const getSafeUrl = (url) => {
  if (typeof url !== "string") return null;

  const normalizedUrl = url.trim();
  if (!normalizedUrl) return null;

  let parsedUrl;
  try {
    parsedUrl = new URL(normalizedUrl, window.location.href);
  } catch {
    return null;
  }

  return SAFE_LINK_PROTOCOLS.has(parsedUrl.protocol) ? parsedUrl : null;
};

// Decodes HTML entities in a string
export const decodeHTMLEntities = (text) => {
  const parser = new DOMParser();
  return parser.parseFromString(text, "text/html").body.textContent;
};

// Transforms field keys to lowercase in an array of objects
export const transformFieldsToLowercase = (data) => {
  return data.map((item) =>
    Object.keys(item).reduce((acc, key) => {
      acc[key.toLowerCase()] = item[key]; // Convert key to lowercase
      return acc;
    }, {})
  );
};

// Formats CTA (Call to Action) by extracting title and URL from HTML string
export const formatCta = (dataArray) => {
  return dataArray.map((obj) => {
    const parser = new DOMParser();
    const ctaDoc = parser.parseFromString(obj.cta, "text/html");

    const linkTitle = ctaDoc.querySelector(".link-title");
    const linkUrl = ctaDoc.querySelector(".link-url a");

    return {
      ...obj,
      cta: {
        title: linkTitle ? linkTitle.textContent : "",
        url: linkUrl ? linkUrl.getAttribute("href") : "",
      },
    };
  });
};

// Transforms a menu from HTML string to an array of objects with label and URL
export const transformMenu = (htmlString) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  const links = doc.querySelectorAll("a");

  return Array.from(links).map((link) => ({
    label: link.textContent,
    url: checkURL(link.href), // Check and transform URL
  }));
};

// Checks whether a URL points to an external origin
export const isExternalLink = (url) => {
  const parsedUrl = getSafeUrl(url);

  return Boolean(parsedUrl && parsedUrl.origin !== window.location.origin);
};

// Normalizes internal URLs for application navigation
export const checkURL = (url) => {
  const normalizedUrl = typeof url === "string" ? url.trim() : "";
  const parsedUrl = getSafeUrl(normalizedUrl);

  if (!parsedUrl) return "";
  if (parsedUrl.origin !== window.location.origin) return normalizedUrl;

  let linkTarget = parsedUrl.pathname + parsedUrl.search + parsedUrl.hash;

  if (linkTarget.startsWith("/drupal")) {
    linkTarget = linkTarget.slice(7); // Remove '/drupal' if present
  }

  return linkTarget; // Return original URL if not external or '/drupal'
};

export const getLinkDetails = (url) => {
  const normalizedUrl = typeof url === "string" ? url.trim() : "";

  return {
    isExternalLink: isExternalLink(normalizedUrl),
    linkTarget: checkURL(normalizedUrl),
  };
};

export const formatBody = (body) => {
  if (!body) return "";
  return `<p>${body.replace(/\n+/g, "<br>")}</p>`;
};

// Fetches a file URL based on the filename query
export const getFile = async (query) => {
  const fetchURL = pldtConfig.apiUrl + `api/v1/get-file?filename=${query}`;

  try {
    const response = await fetch(fetchURL);
    const data = await response.json();
    const filename = data[0].filename;
    const parser = new DOMParser();
    const doc = parser.parseFromString(filename, "text/html");
    const linkElement = doc.querySelector("a");
    return linkElement ? linkElement.getAttribute("href") : "";
  } catch (err) {
    console.error(err.message);
    return "";
  }
};

// Drupal field parser
export const parseDrupalFields = (htmlString, fields) => {
  if (!htmlString) return {};

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  const result = {};

  Object.entries(fields).forEach(([key, field]) => {
    const element = doc.querySelector(field.selector);

    if (!element) {
      result[key] = "";
      return;
    }

    if (field.type === "image") {
      result[key] = element.getAttribute("src") || "";
      return;
    }

    if (field.allowHTML) {
      result[key] = element.innerHTML.trim();
      return;
    }

    result[key] = element.textContent.trim();
  });

  return result;
};

export const parseDrupalRepeater = (htmlString, itemSelector, fields) => {
  if (!htmlString) return [];

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");

  return Array.from(doc.querySelectorAll(itemSelector)).map((el) => {
    const result = {};
    for (const key in fields) {
      const { selector, type = "text" } = fields[key];
      const target = el.querySelector(selector);

      if (!target) continue;

      if (type === "image") {
        result[key] = target.getAttribute("src") || "";
      } else if (type === "html") {
        result[key] = target.innerHTML.trim();
      } else {
        result[key] = target.textContent.trim();
      }

      // remove empty optional fields
      if (result[key] === "") delete result[key];
    }
    return result;
  });
};

export const isInternalLink = (url) => {
  if (!url) return false;
  try {
    const hostname = window.location.hostname;
    return url.startsWith('/') || url.includes(hostname);
  } catch {
    return false;
  }
};

// Decode WYSIWYG content with HTML entities
export const decodeWYSIWYGContent = (text) => {
  if (!text) return "";
  const txt = document.createElement('textarea');
  txt.innerHTML = text;
  return txt.value;
};

export const attachDomainToImages = (html, domain) => {
  if (!html) return "";
  return html.replace(/<img([^>]+)src="(\/[^"]+)"/g, `<img$1src="${domain}$2"`);
};
